import { useEffect, useState } from 'react'
import { Button, Spinner } from './components/ui'
import { storedLocale, useI18n } from './i18n'
import { Auth } from './screens/Auth'
import { CalendarScreen } from './screens/CalendarScreen'
import { Home } from './screens/Home'
import { Settings } from './screens/Settings'
import { Stats } from './screens/Stats'
import { Us } from './screens/Us'
import { useStore } from './store'

type Tab = 'home' | 'calendar' | 'stats' | 'us' | 'settings'

const TABS: { key: Tab; icon: (active: boolean) => React.ReactNode }[] = [
  { key: 'home', icon: (a) => <IconRing active={a} /> },
  { key: 'calendar', icon: (a) => <IconCalendar active={a} /> },
  { key: 'stats', icon: (a) => <IconChart active={a} /> },
  { key: 'us', icon: (a) => <IconHeart active={a} /> },
  { key: 'settings', icon: (a) => <IconGear active={a} /> },
]

export function App() {
  const { status, error, snapshot, reload, syncLocale } = useStore()
  const { t, locale, adoptRemote } = useI18n()

  // 语言在设备和账号之间对齐一次。
  //
  // 界面语言以设备为准（人就在这台设备前面，他的选择最新）；账号上那份是给
  // 邮件用的，也是换新设备时的默认值。所以：这台设备上选过 → 推给服务端；
  // 没选过 → 采纳账号上那门语言。
  useEffect(() => {
    if (!snapshot) return
    if (storedLocale()) {
      if (snapshot.user.locale !== locale) {
        // 失败无所谓，界面照常，下次打开还会再试
        void syncLocale(locale).catch(() => {})
      }
    } else {
      adoptRemote(snapshot.user.locale)
    }
  }, [snapshot, locale, adoptRemote, syncLocale])

  // 伴侣默认落在「我们」页，记录周期的一方落在首页 —— 两个人关心的东西不一样
  const [tab, setTab] = useState<Tab>('home')
  const [landed, setLanded] = useState(false)

  // 一个人用的时候不显示「我们」：那一整页讲的是「TA 今天怎么样、你该怎么配合」，
  // 只有自己一个人时，那个「TA」就是自己，纯属添乱。
  // 邀请伴侣进来之后这一栏会自己出现。
  const solo = (snapshot?.members.length ?? 1) < 2
  const tabs = solo ? TABS.filter((x) => x.key !== 'us') : TABS

  useEffect(() => {
    if (snapshot && !landed) {
      setTab(snapshot.user.role === 'partner' && !solo ? 'us' : 'home')
      setLanded(true)
    }
  }, [snapshot, landed, solo])

  // 伴侣退出之后，本来停在「我们」页的人得挪走，否则会卡在一个已经不在导航里的页面
  useEffect(() => {
    if (solo && tab === 'us') setTab('home')
  }, [solo, tab])

  if (status === 'loading') {
    return (
      <div className="min-h-full grid place-items-center text-muted">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  if (status === 'anonymous') return <Auth />

  if (status === 'error' || !snapshot) {
    return (
      <div className="min-h-full grid place-items-center px-8 text-center">
        <div>
          <p className="text-muted mb-5">{error ?? t.common.error}</p>
          <Button variant="soft" onClick={() => void reload()}>
            {t.common.retry}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full flex flex-col">
      <main className="flex-1 max-w-lg w-full mx-auto">
        {tab === 'home' && <Home />}
        {tab === 'calendar' && <CalendarScreen />}
        {tab === 'stats' && <Stats />}
        {tab === 'us' && <Us />}
        {tab === 'settings' && <Settings />}
      </main>

      <nav className="sticky bottom-0 bg-card/85 backdrop-blur-xl border-t border-line/70 safe-bottom">
        <div className="max-w-lg mx-auto flex">
          {tabs.map((item) => {
            const active = tab === item.key
            return (
              <button
                key={item.key}
                onClick={() => setTab(item.key)}
                aria-current={active ? 'page' : undefined}
                className={`flex-1 pt-2 pb-1 flex flex-col items-center gap-1 transition ${
                  active ? 'text-accent' : 'text-muted'
                }`}
              >
                {item.icon(active)}
                <span className="text-[10px]">{t.nav[item.key]}</span>
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

// 图标手写成 SVG，不引图标库 —— 五个图标不值得为它增加一个依赖
const stroke = { stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const }

function IconRing({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="7.5" {...stroke} opacity={active ? 1 : 0.85} />
      <circle cx="11" cy="3.5" r={active ? 2.2 : 1.8} fill="currentColor" />
    </svg>
  )
}

function IconCalendar({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="3.5" y="5" width="15" height="13.5" rx="3" {...stroke} />
      <path d="M3.5 9h15" {...stroke} />
      <path d="M7.5 3v3.5M14.5 3v3.5" {...stroke} />
      {active && <circle cx="11" cy="13.5" r="1.8" fill="currentColor" />}
    </svg>
  )
}

function IconChart({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M4 14.5l4.5-5 3.5 3 5.5-6.5" {...stroke} strokeLinejoin="round" />
      {active && <circle cx="17.5" cy="6" r="2" fill="currentColor" />}
      <path d="M3.5 18.5h15" {...stroke} opacity="0.45" />
    </svg>
  )
}

function IconHeart({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill={active ? 'currentColor' : 'none'}>
      <path
        d="M11 18s-6.5-4-6.5-8.2A3.8 3.8 0 0 1 11 7.2a3.8 3.8 0 0 1 6.5 2.6C17.5 14 11 18 11 18z"
        {...stroke}
        strokeLinejoin="round"
      />
    </svg>
  )
}

function IconGear({ active }: { active: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r={active ? 3 : 2.6} fill={active ? 'currentColor' : 'none'} {...stroke} />
      <path
        d="M11 2.8v2M11 17.2v2M19.2 11h-2M4.8 11h-2M16.8 5.2l-1.4 1.4M6.6 15.4l-1.4 1.4M16.8 16.8l-1.4-1.4M6.6 6.6L5.2 5.2"
        {...stroke}
      />
    </svg>
  )
}
